import { createSlice } from '@reduxjs/toolkit';
//
import { uniqBy } from 'lodash';
import moment from 'moment';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { dispatch, store } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { isQuickReplyTemplate } from '../../components/whatsappComponents/quickReplyUtils';
import { campaignActionApproval } from '../../utils/campaignTemplateApproval';
import { getConnectedWhatsappBusinessAccounts } from '../../utils/whatsappAccountLifecycle';

// All Practices phase-2: pull the selection off the store each call so
// campaign list endpoints aggregate when the user has multi-practice mode on.
const _activePracticesSuffix = () =>
    practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);

export const campaignRequestScopeKey = ({ practiceId, id }) =>
    [practiceId, id].map((value) => String(value ?? '')).join(':');

let whatsappTemplatePracticeOwnerId = null;
let whatsappTemplateRequestGeneration = 0;

const normalizePracticeId = (practiceId) => (
    practiceId === null || practiceId === undefined || practiceId === ''
        ? null
        : String(practiceId)
);

const captureWhatsappTemplateRequest = (practiceId) => {
    const practiceOwnerId = normalizePracticeId(practiceId);
    const requestGeneration = whatsappTemplateRequestGeneration;
    const isCurrent = () => practiceOwnerId === whatsappTemplatePracticeOwnerId
        && requestGeneration === whatsappTemplateRequestGeneration;
    return {
        isCurrent,
        commit(action) {
            if (isCurrent()) dispatch(action);
        },
    };
};

// -----------------------------------------------

const initialState = {
    isLoading: false,
    isLoadingFinish: false,
    loadMoreLoading: false,
    campaignsData: null,
    newCampaign: {
        campaignName: null,
        engagement_template_tab: 'All',
        audienceSelected: null
    },
    selectedCampaign: {},
    conditionList: [],
    markAsComplete: false,
    campaignReport: null,
    campaignReportLoading: false,
    campaignReportCampaignId: null,
    campaignReportScopeKey: null,
    campaignReportRequestId: null,
    campaignTimeline: null, // { count, results: [{id, recipient, status, timestamp, error, lead_pk}] }
    campaignTimelineLoading: false,
    campaignTimelineQueryKey: null,
    campaignTimelineRequestId: null,
    campaignLeadData: {
        results: []
    },
    campaignData: {},
    campaignCounter: [],
    fileUploadError: '',
    finalCampaignData: {},
    isIncludeArchive: false,
    mediaImages: {
        results: [],
        count: 0
    },
    basicBody: '',
    tempcampaignData: {},
    currentActiveTab: 'Existing audience',
    usedIn: '',
    loading: false,
    whatsAppTemplateLoading: false,
    whatsAppTemplate: {},
    allWhatsappTemplates: [],
    allWhatsappTemplateLoading: false,
    allWhatsappTemplatesPracticeId: null,
    campaignDetail: null,
    campaignDetailLoading: false,
    campaignDetailScopeKey: null,
    campaignDetailRequestId: null
};

const slice = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoadingData(state, action) {
            state.isLoadingFinish = action.payload;
        },
        clearNewCampaignData(state, action) {
            state.markAsComplete = false;
            if (!action.payload?.isPreview) {
                state.selectedCampaign = {};
                state.newCampaign = {
                    campaignName: null,
                    engagement_template_tab: 'All'
                };
            }
            state.conditionList = [];
            state.finalCampaignData = {};
            state.isIncludeArchive = false;
        },
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },
        setCampaignListing(state, action) {
            state.isLoading = false;
            state.campaignsData = action.payload;
        },
        saveNewCampaign(state, action) {
            state.isLoading = false;
            state.newCampaign = {
                ...state.newCampaign,
                ...action.payload
            };
        },
        updateCampaignStep(state, action) {
            state.markAsComplete = action.payload;
        },
        updateSelectedCampaign(state, action) {
            state.selectedCampaign = action.payload;
        },
        updateConditionList(state, action) {
            state.conditionList = action.payload;
        },
        startCampaignReportLoading(state, action) {
            const { practiceId, campaignId, requestId } = action.payload;
            const scopeKey = action.payload.scopeKey || campaignRequestScopeKey({ practiceId, id: campaignId });
            if (state.campaignReportScopeKey !== scopeKey) {
                state.campaignReport = null;
            }
            state.campaignReportLoading = true;
            state.campaignReportCampaignId = campaignId;
            state.campaignReportScopeKey = scopeKey;
            state.campaignReportRequestId = requestId;
        },
        setCampaignReport(state, action) {
            if (action.payload === null) {
                state.campaignReport = null;
                state.campaignReportLoading = false;
                state.campaignReportCampaignId = null;
                state.campaignReportScopeKey = null;
                state.campaignReportRequestId = null;
                return;
            }
            const { practiceId, campaignId, requestId, report } = action.payload;
            const scopeKey = action.payload.scopeKey || campaignRequestScopeKey({ practiceId, id: campaignId });
            if (
                state.campaignReportScopeKey !== scopeKey
                || state.campaignReportRequestId !== requestId
            ) return;
            state.campaignReportLoading = false;
            if (report !== undefined) state.campaignReport = report;
        },
        setCampaignTimeline(state, action) {
            const { queryKey, requestId, timeline } = action.payload;
            if (
                state.campaignTimelineQueryKey !== queryKey
                || state.campaignTimelineRequestId !== requestId
            ) return;
            if (timeline !== undefined) state.campaignTimeline = timeline;
            state.campaignTimelineLoading = false;
        },
        startCampaignTimelineLoading(state, action) {
            const { queryKey, requestId } = action.payload;
            if (state.campaignTimelineQueryKey !== queryKey) {
                state.campaignTimeline = null;
            }
            state.campaignTimelineQueryKey = queryKey;
            state.campaignTimelineRequestId = requestId;
            state.campaignTimelineLoading = true;
        },
        setCampaignLeadData(state, action) {
            state.isLoading = false;
            state.campaignLeadData = action.payload;
        },
        setCampaignData(state, action) {
            state.isLoading = false;
            state.campaignData = action.payload;
        },
        updateCounter(state, action) {
            state.campaignCounter = action.payload;
        },
        setMediaImages(state, action) {
            state.mediaImages.results = action.payload?.results;
            state.mediaImages.count = action.payload?.count;
        },
        clearMediaImages(state) {
            state.mediaImages.results = [];
            state.mediaImages.count = 0;
        },
        setLoadMoreLoading(state, action) {
            state.loadMoreLoading = action.payload;
        },
        setFileUploadError(state, action) {
            state.fileUploadError = action.payload;
        },
        setFinalCampaignData(state, action) {
            state.finalCampaignData = action.payload;
        },
        setIncludeArchive(state, action) {
            state.isIncludeArchive = action.payload;
        },
        setBasicBody(state, action) {
            state.basicBody = action.payload;
        },
        setTempcampaignData(state, action) {
            state.tempcampaignData = action.payload;
        },
        setCurrentTab(state, action) {
            state.currentActiveTab = action.payload;
        },
        setUsedIn(state, action) {
            state.usedIn = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setPreviewWhatsappStart(state, action) {
            state.whatsAppTemplateLoading = action.payload?.loading;
            state.whatsAppTemplate = action.payload?.template;
        },
        setAllWhatsappTemplateLoading(state, action) {
            state.allWhatsappTemplateLoading = action?.payload
        },
        setAllWhatsappTemplates(state, action) {
            state.allWhatsappTemplates = action?.payload;
        },
        bindWhatsappTemplatePractice(state, action) {
            if (state.allWhatsappTemplatesPracticeId === action.payload) return;
            state.allWhatsappTemplatesPracticeId = action.payload;
            state.allWhatsappTemplates = [];
            state.allWhatsappTemplateLoading = false;
        },
        setCampaignDetail(state, action) {
            const { practiceId, campaignId } = action.payload;
            const scopeKey = action.payload.scopeKey || campaignRequestScopeKey({ practiceId, id: campaignId });
            if (
                String(state.campaignDetailRequestId) !== String(action.payload.id)
                || state.campaignDetailScopeKey !== scopeKey
            ) return;
            state.campaignDetailLoading = false;
            const retainedDetailIsCurrent = String(state.campaignDetail?.id) === String(campaignId);
            if (action.payload.detail !== null || !retainedDetailIsCurrent) {
                state.campaignDetail = action.payload.detail;
            }
        },
        startCampaignDetailLoading(state, action) {
            const { practiceId, campaignId, requestId } = action.payload;
            const scopeKey = action.payload.scopeKey || campaignRequestScopeKey({ practiceId, id: campaignId });
            state.campaignDetailLoading = true;
            if (state.campaignDetailScopeKey !== scopeKey) {
                state.campaignDetail = null;
            }
            state.campaignDetailScopeKey = scopeKey;
            state.campaignDetailRequestId = requestId;
        }
    }
});

export default slice.reducer;

export const { setCampaignListing, reset, startLoading, saveNewCampaign, updateCampaignStep, updateSelectedCampaign, clearNewCampaignData, updateConditionList, setCampaignReport, startCampaignReportLoading, startLoadingData, setCampaignLeadData, setCampaignData, updateCounter, setMediaImages, setLoadMoreLoading, setFileUploadError, clearMediaImages, setFinalCampaignData, setIncludeArchive, setBasicBody, setTempcampaignData, setCurrentTab, setUsedIn, setCampaignDetail, startCampaignDetailLoading, setCampaignTimeline, startCampaignTimelineLoading } = slice.actions;

export function bindWhatsappTemplatePractice(practiceId) {
    const practiceOwnerId = normalizePracticeId(practiceId);
    if (practiceOwnerId !== whatsappTemplatePracticeOwnerId) {
        whatsappTemplatePracticeOwnerId = practiceOwnerId;
        whatsappTemplateRequestGeneration += 1;
    }
    return slice.actions.bindWhatsappTemplatePractice(practiceOwnerId);
}

let campaignRequestSequence = 0;
const nextCampaignRequestId = (scope) => {
    campaignRequestSequence += 1;
    return `${scope}-${campaignRequestSequence}`;
};
export const campaignTimelineQueryKey = ({ practiceId, id, statusFilter, limit = 25, offset = 0 }) =>
    [campaignRequestScopeKey({ practiceId, id }), statusFilter || 'ALL', limit, offset].map(String).join(':');

const requestIsCurrent = ({ requestField, requestId, scopeField, scopeKey }) => {
    const campaignState = store.getState().campaign;
    return campaignState?.[requestField] === requestId && campaignState?.[scopeField] === scopeKey;
};

const getCampaign = dt => {
    if (dt === "All Campaigns") return "";
    if (dt === "Whatsapp Campaigns") return "WHATSAPP";
    if (dt === "Email Campaigns") return "EMAIL";
    if (dt === "Sms Campaigns") return "SMS";
    return "";
}

export function getCampaignListing(data, handleClose) {
    return async () => {
        if (data?.returnOnly) {
            return callApi(`/api/v1/practices/${data.practiceId}/campaigns/?${data.query}${_activePracticesSuffix()}`);
        }
        try {
            dispatch(slice.actions.setLoading(true));
            const limit = data?.limit ? `&limit=${data?.limit}` : '';
            const campaignFilter = getCampaign(data?.campaignType) ? `&channel=${getCampaign(data?.campaignType)}` : "";
            const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
            const status = data.status ? `&status__in=${data?.status}` : '';
            const searchQuery = data.searchQuery ? `&name__icontains=${encodeURIComponent(data.searchQuery)}` : '';
            const param = `${limit}${offset}${status}${searchQuery}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?${campaignFilter}&ordering=-created_at${param}${_activePracticesSuffix()}`);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignListing(response.data));
                if (handleClose) {
                    handleClose(response.data.count);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
        return undefined;
    };
}
export function getCampaignListingCounter(data, handleClose) {
    return async () => {
        if (data?.returnOnly) {
            const query = new URLSearchParams(data.query);
            query.set('limit', '1');
            query.set('offset', '0');
            query.set('fields', '{}');
            query.delete('status__in');
            if (data.status && data.status !== 'All') query.set('status__in', data.status);
            return callApi(`/api/v1/practices/${data.practiceId}/campaigns/?${query}${_activePracticesSuffix()}`);
        }
        try {
            const limit = data?.limit ? `&limit=${data?.limit}` : '';
            const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
            const campaignFilter = getCampaign(data?.campaignType) ? `&channel=${getCampaign(data?.campaignType)}` : "";
            const status = data.status && data.status !== 'All' ? `&status__in=${data?.status}` : '';
            const searchQuery = data.searchQuery ? `&name__icontains=${encodeURIComponent(data.searchQuery)}` : '';
            const param = `${limit}${offset}${status}${searchQuery}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?${campaignFilter}&ordering=-created_at${param}&fields={}${_activePracticesSuffix()}`);
            if (response.status === 200) {
                const { campaignCounter } = store.getState().campaign;
                dispatch(slice.actions.updateCounter([...campaignCounter, { filterStatus: data.status, count: response.data.count }]));
                if (handleClose) {
                    handleClose(response.data.count);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
        return undefined;
    };
}

// export function getCounterCampaign(data) {
//     return async () => {
//         try {
//             const limit = data?.limit ? `&limit=${data?.limit}` : '';
//             const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
//             const status = data.status ? `&lead_treatments__status__in=${data?.status}` : '';
//             const search = `${limit}${offset}${status}`;
//             const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?ordering=-created_at${search}&fields={}`);
//             if (response.status === 200) {
//                 const { counter } = store.getState().lead;
//             } else {
//                 dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
//             }
//         } catch (error) {
//             dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
//         }
//     }
// }

export function deleteCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/`, 'delete');
            if (response.status === 204) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign deleted successfully' || 'Something went wrong' }));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createCampaign(data, handleClose, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Campaign created successfully' || 'Something went wrong' }));
                dispatch(updateSelectedCampaign(response.data));
                dispatch(slice.actions.setCampaignData(response.data));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                    dispatch(updateCampaignStep(false));
                }
                if (type === 'duplicateEvent') {
                    handleClose();
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                    dispatch(updateCampaignStep(false));
                }
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function previewRecepientsCampaign(data, id, fieldParams) {
    return async () => {
        try {
            const limit = fieldParams?.limit ? `&limit=${fieldParams?.limit}` : '';
            const offset = (fieldParams?.offset === 0 || fieldParams?.offset) ? `&offset=${fieldParams?.offset}` : '';
            const param = `${limit}${offset}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${id}/preview_recepients/?ordering=-created_at${param}`, 'post', data);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignLeadData(response.data));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
            } else {
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateCampaign(data, id, handleClose, handleSaveClose, sendCampaign, isTagUpdate) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${id}/`, 'put', data);
            if (response.status === 200) {
                if (handleSaveClose && sendCampaign) {
                    const persistedResponse = await callApi(
                        `/api/v1/practices/${data?.practiceId}/campaigns/${id}/`,
                        'get',
                        null,
                        false,
                        { bustCache: true }
                    );
                    if (persistedResponse.status !== 200) {
                        dispatch(updateError({ success: false, message: 'Campaign template eligibility could not be verified. Refresh the campaign before continuing.' }));
                        dispatch(startLoadingData(false));
                        dispatch(updateCampaignStep(false));
                        return;
                    }
                    const approval = campaignActionApproval(persistedResponse.data);
                    if (!approval.allowed) {
                        dispatch(updateError({ success: false, message: approval.reason }));
                        dispatch(startLoadingData(false));
                        dispatch(updateCampaignStep(false));
                        return;
                    }
                }
                dispatch(updateError({ success: true, message: 'Campaign updated successfully' || 'Something went wrong' }));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                }
                if (handleSaveClose) {
                    handleClose(sendCampaign, response?.data?.id);
                } else if (isTagUpdate) {
                    handleClose('tag');
                } else {
                    handleClose(response?.data);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
                // Call callback with null to indicate failure
                if (handleClose && typeof handleClose === 'function' && !handleSaveClose && !isTagUpdate) {
                    handleClose(null);
                }
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(startLoadingData(false));
            dispatch(updateCampaignStep(false));
            // Call callback with null to indicate failure
            if (handleClose && typeof handleClose === 'function' && !handleSaveClose && !isTagUpdate) {
                handleClose(null);
            }
        }
    };
}

export function cancelCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/cancel/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign cancelled successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isAlreadyCancelled = msg.includes('CANCELLED') || msg.includes('TransitionNotAllowed');
                dispatch(updateError({ success: isAlreadyCancelled, message: isAlreadyCancelled ? 'Campaign is already cancelled' : msg }));
                if (isAlreadyCancelled) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function scheduleCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/schedule/`, 'post');
            if (response.status === 200) {
                handleClose('schedule');
                dispatch(updateError({ success: true, message: 'Campaign scheduled successfully', id: data.id }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('SCHEDULED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is already scheduled' : msg }));
                if (isTransitionError) handleClose('schedule');
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getCampaignReport(data) {
    return async () => {
        const scopeKey = campaignRequestScopeKey({ practiceId: data?.practiceId, id: data?.id });
        const requestId = nextCampaignRequestId('report');
        dispatch(slice.actions.startCampaignReportLoading({
            practiceId: data?.practiceId,
            campaignId: data?.id,
            scopeKey,
            requestId,
        }));
        try {
            const [reportResponse, campaignResponse] = await Promise.all([
                callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data?.id}/report/`),
                callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data?.id}/`),
            ]);
            if (reportResponse.status === 200) {
                const reportList = reportResponse.data?.results || reportResponse.data || [];
                const campaign = campaignResponse.data || {};
                const channel = campaign.channel || 'EMAIL';

                // Transform list of {event/status, count} into flat object
                const reportData = { channel, campaignName: campaign.name || '' };
                if (Array.isArray(reportList)) {
                    reportList.forEach((item) => {
                        const key = item.event || item.status;
                        if (key) {
                            reportData[key] = (reportData[key] || 0) + (item.count || 0);
                        }
                    });
                }

                dispatch(slice.actions.setCampaignReport({
                    practiceId: data?.practiceId,
                    campaignId: data?.id,
                    scopeKey,
                    requestId,
                    report: reportData,
                }));
            } else if (requestIsCurrent({
                requestField: 'campaignReportRequestId',
                requestId,
                scopeField: 'campaignReportScopeKey',
                scopeKey,
            })) {
                dispatch(updateError({ success: false, message: JSON.stringify(reportResponse.data) || 'Something went wrong' }));
                dispatch(slice.actions.setCampaignReport({
                    practiceId: data?.practiceId,
                    campaignId: data?.id,
                    scopeKey,
                    requestId,
                    report: undefined,
                }));
            }
        } catch (error) {
            if (requestIsCurrent({
                requestField: 'campaignReportRequestId',
                requestId,
                scopeField: 'campaignReportScopeKey',
                scopeKey,
            })) {
                dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
                dispatch(slice.actions.setCampaignReport({
                    practiceId: data?.practiceId,
                    campaignId: data?.id,
                    scopeKey,
                    requestId,
                    report: undefined,
                }));
            }
        }
    };
}

/**
 * Fetch per-recipient send timeline for a campaign.
 *
 * Hits the new `/campaigns/{id}/timeline/` endpoint which returns paginated
 * ScheduledMessage rows ordered by activity DESC, each row carrying derived
 * channel-agnostic ``status`` (pending/sent/skipped/failed) + ``timestamp``
 * + ``error`` fields. Used by the CampaignLaunchSuccess live-progress view
 * and any future "drill into per-recipient detail" surface.
 *
 * Errors are NOT surfaced as toasts because this fires every 5s during
 * polling — a transient blip shouldn't spam the user. Silent on failure.
 */
export function getCampaignTimeline({ practiceId, id, statusFilter, limit = 25, offset = 0 }) {
    return async () => {
        if (!practiceId || !id) return;
        const queryKey = campaignTimelineQueryKey({ practiceId, id, statusFilter, limit, offset });
        const requestId = nextCampaignRequestId('timeline');
        dispatch(slice.actions.startCampaignTimelineLoading({ queryKey, requestId }));
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (limit) params.set('limit', String(limit));
            if (offset) params.set('offset', String(offset));
            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/campaigns/${id}/timeline/${qs}`);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignTimeline({ queryKey, requestId, timeline: response.data }));
            } else if (requestIsCurrent({
                requestField: 'campaignTimelineRequestId',
                requestId,
                scopeField: 'campaignTimelineQueryKey',
                scopeKey: queryKey,
            })) {
                dispatch(slice.actions.setCampaignTimeline({ queryKey, requestId, timeline: undefined }));
            }
            // Silent on non-200; the next 5s tick will retry.
        } catch (error) {
            if (requestIsCurrent({
                requestField: 'campaignTimelineRequestId',
                requestId,
                scopeField: 'campaignTimelineQueryKey',
                scopeKey: queryKey,
            })) {
                dispatch(slice.actions.setCampaignTimeline({ queryKey, requestId, timeline: undefined }));
            }
        }
    };
}

/**
 * Re-queue a single failed ScheduledMessage for re-send.
 *
 * Backs the per-row "Retry" button on the send timeline. The backend
 * resets sent_count + last_send_attempt_result on the SM row and fires
 * the send_message Celery task again. The next /timeline/ poll will
 * show the row drop back to 'pending' then settle to its new terminal
 * state.
 *
 * @param {{practiceId: number, id: number, smId: number}} params
 * @param {() => void} onDone fires after the API call resolves (success or fail)
 *                            so the UI can clear the row's "retrying" spinner.
 */
export function retryRecipient({ practiceId, id, smId }, onDone) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/${id}/retry-recipient/${smId}/`,
                'post',
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Retrying recipient…' }));
            } else {
                dispatch(updateError({ success: false, message: 'Retry failed' }));
            }
        } catch (_) {
            dispatch(updateError({ success: false, message: 'Retry failed' }));
        } finally {
            if (onDone) onDone();
        }
    };
}

/**
 * Re-queue every failed ScheduledMessage for a campaign at once.
 *
 * Backs the "Retry failed (N)" button on the report page header.
 * The backend re-enqueues all SMs with a non-Success / non-Skipped
 * last attempt result.
 */
export function retryFailedRecipients({ practiceId, id }, onDone) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/${id}/retry-failed/`,
                'post',
            );
            if (response.status === 200) {
                const n = response.data?.retried ?? 0;
                dispatch(updateError({
                    success: n > 0,
                    message: n > 0
                        ? `Retrying ${n.toLocaleString()} recipient${n === 1 ? '' : 's'}…`
                        : 'No failed recipients to retry',
                }));
                if (onDone) onDone(n);
            } else {
                dispatch(updateError({ success: false, message: 'Retry failed' }));
                if (onDone) onDone(0);
            }
        } catch (_) {
            dispatch(updateError({ success: false, message: 'Retry failed' }));
            if (onDone) onDone(0);
        }
    };
}

export function sendEngagementTestMail({ practiceId, tempLateId, detail, callback }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/engagement-templates/${tempLateId}/send_test/`, 'post', detail);
            // BE now executes test sends SYNCHRONOUSLY and reports back
            // ``sent`` (bool) + ``result`` (str). 200 = success; 502 =
            // queued but actual provider delivery failed — surface the
            // real result so operators don't get a green snackbar for a
            // silently-dropped send.
            const sent = response?.data?.sent;
            const result = response?.data?.result;
            if (response.status === 200 && sent === true) {
                callback({ success: true, message: 'Test email sent successfully' });
            } else if (sent === false) {
                callback({
                    success: false,
                    message: `Test send failed: ${result || 'unknown error'}`,
                });
            } else {
                callback({ success: false, message: response.data });
            }
        } catch (error) {
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function getMediaImages({ practiceId, shouldReset, limit = 50, offset = 0 }) {
    return async () => {
        try {
            dispatch(setLoadMoreLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/media/?offset=${offset}&limit=${limit}&ordering=-created_at`);
            const { mediaImages } = store.getState().campaign;
            if (response.status === 200) {
                if (shouldReset) {
                    dispatch(slice.actions.setMediaImages({ results: response.data.results, count: response.data?.count }));
                    dispatch(setLoadMoreLoading(false));
                    return;
                }
                dispatch(setLoadMoreLoading(false));
                const images = [...mediaImages.results, ...response.data.results];
                const imagesList = uniqBy(images, 'id');
                const sortedImages = imagesList?.sort((a, b) => moment(b.created_at, 'DD-MM-YYYY HH:mm:ss') - moment(a.created_at, 'DD-MM-YYYY HH:mm:ss'));
                dispatch(slice.actions.setMediaImages({ results: sortedImages, count: response.data?.count }));
            } else {
                dispatch(setLoadMoreLoading(false));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(setLoadMoreLoading(false));
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function deleteImage({ practiceId, id }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/media/${id}`, 'delete');
            if (response.status === 204) {
                dispatch(getMediaImages({ practiceId, limit: 50, offset: 0, shouldReset: true }));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function pauseCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/pause/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign paused successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('PAUSED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is already paused' : msg }));
                if (isTransitionError) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}


export function resumeCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/resume/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign resumed successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('PAUSED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is not paused' : msg }));
                if (isTransitionError) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getWhatsappTemplates(data) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK or template ID is missing
        if (!data?.practiceId || !data?.phone || !data?.templateId) return;
        try {
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: true, template: {} }));
            const response = await callApi(
                `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${data?.phone}/whatsapp-message-templates/${data?.templateId}`
            );
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: false, template: response?.data }));
        } catch (err) {
            console.error("Failed to fetch whatsapp template", err);
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: false, template: {} }));
        }
    }
}

export function getAllWhatsappTemplates(data) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if practiceId is missing
        if (!data?.practiceId) return;
        const request = captureWhatsappTemplateRequest(data.practiceId);
        if (!request.isCurrent()) return;
        try {
            request.commit(slice.actions.setAllWhatsappTemplateLoading(true));
            const availableWhatsaAppBusinessAccResp = await callApi(
                `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/`,
                'GET'
            );
            if (!request.isCurrent()) return;
            const { count, results } = availableWhatsaAppBusinessAccResp?.data ?? {};
            if (count === 0) {
                request.commit(updateError({ success: "warning", message: "No Whatsapp Business Account set for this Practice" }));
                return;
            }
            const whatsappTemplates = await Promise.all(
                getConnectedWhatsappBusinessAccounts(results).map(async (businessAccount) => {
                    if (!request.isCurrent()) return [];
                    const { name, id, reviewStatus } = businessAccount ?? {};

                    await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/sync_templates/`,
                        'POST'
                    );
                    if (!request.isCurrent()) return [];

                    const phonesAvailable = await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/`,
                        'GET'
                    );
                    if (!request.isCurrent()) return [];

                    const { display_phone_number: displayPhoneNumber, id: phoneId } = phonesAvailable?.data?.results?.[0] ?? {};

                    const templateResponse = await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/whatsapp-message-templates/?&ordering=-created_at`
                    );
                    if (!request.isCurrent()) return [];

                    return templateResponse?.data?.results?.map((template) => ({
                        ...template,
                        businessName: name,
                        businessId: phoneId,
                        businessReviewStatus: reviewStatus,
                        displayPhoneNumber,
                    })) ?? [];
                })
            );

            // Flatten the nested arrays if needed. Strip Personal Reply templates
            // — reserved for the closed-window composer, never in the campaign list.
            const flattenedTemplates = whatsappTemplates
                .flat()
                .filter((t) => !isQuickReplyTemplate(t));
            request.commit(slice.actions?.setAllWhatsappTemplates(flattenedTemplates));
        } catch (error) {
            if (request.isCurrent()) {
                console.error("Failed to get all whatsapp templates", error);
                request.commit(updateError({ success: false, message: "Error in fetching whatsapp templates" }));
            }
        } finally {
            request.commit(slice.actions.setAllWhatsappTemplateLoading(false));
        }
    }
}

export function getCampaignDetail({ practiceId, id }) {
    return async () => {
        const scopeKey = campaignRequestScopeKey({ practiceId, id });
        const requestId = nextCampaignRequestId('detail');
        try {
            dispatch(slice.actions.startCampaignDetailLoading({ practiceId, campaignId: id, scopeKey, requestId }));
            const response = await callApi(`/api/v1/practices/${practiceId}/campaigns/${id}/`, 'get');
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignDetail({
                    id: requestId,
                    practiceId,
                    campaignId: id,
                    scopeKey,
                    detail: response.data,
                }));
            } else if (requestIsCurrent({
                requestField: 'campaignDetailRequestId',
                requestId,
                scopeField: 'campaignDetailScopeKey',
                scopeKey,
            })) {
                dispatch(updateError({ success: false, message: 'Failed to load campaign details' }));
                dispatch(slice.actions.setCampaignDetail({ id: requestId, practiceId, campaignId: id, scopeKey, detail: null }));
            }
        } catch (error) {
            if (requestIsCurrent({
                requestField: 'campaignDetailRequestId',
                requestId,
                scopeField: 'campaignDetailScopeKey',
                scopeKey,
            })) {
                dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
                dispatch(slice.actions.setCampaignDetail({ id: requestId, practiceId, campaignId: id, scopeKey, detail: null }));
            }
        }
    };
}
